/** Stock bookings (receive, issue, return, transfer, correct) shared by inventory and projects. */
import { useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { CatalogItemResponse } from "@offertli/shared";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Modal, SelectInput } from "../../components/ui/controls.js";
import { apiGet, apiGetPage, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { MutationError } from "../shared.js";

export type Location = {
  id: string;
  code: string;
  name: string;
  active: boolean;
  isDefault: boolean;
};

export type Movement = {
  id: string;
  type:
    | "receipt"
    | "issue"
    | "return"
    | "adjustment_in"
    | "adjustment_out"
    | "transfer_in"
    | "transfer_out";
  quantity: string;
  unitCost: string | null;
  note: string | null;
  projectId: string | null;
  createdAt: string;
  catalogItem: { id: string; name: string; sku: string | null; unit?: string };
  location: { id: string; code: string; name: string };
};

export type Page<T> = {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
};

export type Operation =
  "receipt" | "adjustment" | "transfer" | "issue" | "return";

export const PAGE_SIZE = 25;

export function useLocations() {
  return useQuery({
    queryKey: ["inventory-locations"],
    queryFn: () =>
      apiGet<{ data: Location[] }>("/inventory/locations").then(
        (response) => response.data,
      ),
  });
}

/** Which operations the current user may start. */
export function useOperations(): Operation[] {
  const { has } = usePermissions();
  return [
    ...(has("inventory.receive") ? (["receipt"] as const) : []),
    ...(has("inventory.issue") ? (["issue", "return"] as const) : []),
    ...(has("inventory.transfer") ? (["transfer"] as const) : []),
    ...(has("inventory.adjust") ? (["adjustment"] as const) : []),
  ];
}

export const OPERATION_ICON: Record<Operation, string> = {
  receipt: "pi pi-download",
  issue: "pi pi-upload",
  return: "pi pi-replay",
  transfer: "pi pi-arrow-right-arrow-left",
  adjustment: "pi pi-sliders-h",
};

export type Draft = {
  catalogItemId: string;
  locationId: string;
  toLocationId: string;
  projectId: string;
  direction: "in" | "out";
  quantity: string;
  unitCost: string;
  note: string;
};

/**
 * One stock booking. The idempotency key is fixed when the dialog opens, so
 * a retried submit (flaky network, double click) is booked only once.
 */
export function StockOperationDialog({
  operation,
  projectId,
  onClose,
}: {
  operation: Operation;
  /** Fixed project for issues/returns started from a project page. */
  projectId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const locations = useLocations();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const activeLocations = (locations.data ?? []).filter((item) => item.active);
  const [draft, setDraft] = useState<Draft>({
    catalogItemId: "",
    locationId: "",
    toLocationId: "",
    projectId: projectId ?? "",
    direction: "in",
    quantity: "1",
    unitCost: "",
    note: "",
  });
  const locationId =
    draft.locationId ||
    activeLocations.find((item) => item.isDefault)?.id ||
    activeLocations[0]?.id ||
    "";
  const items = useQuery({
    queryKey: ["catalog", "stock-items"],
    queryFn: () =>
      apiGet<Page<CatalogItemResponse>>("/catalog/items", {
        query: { stockTracked: true, active: true, pageSize: 100 },
      }).then((response) => response.data),
  });
  const needsProject = operation === "issue" || operation === "return";
  const projects = useQuery({
    queryKey: ["inventory-projects"],
    enabled: needsProject && !projectId,
    queryFn: () =>
      apiGetPage<{
        data: { id: string; number: string; title: string; status: string };
      }>("/projects", { pageSize: 100 }),
  });
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const save = useMutation({
    mutationFn: () => {
      const base = {
        catalogItemId: draft.catalogItemId,
        quantity: draft.quantity,
        idempotencyKey,
      };
      const note = draft.note.trim() || undefined;
      switch (operation) {
        case "receipt":
          return apiPost("/inventory/receipts", {
            ...base,
            locationId,
            unitCost: draft.unitCost || undefined,
            note,
          });
        case "adjustment":
          return apiPost("/inventory/adjustments", {
            ...base,
            locationId,
            direction: draft.direction,
            unitCost:
              draft.direction === "in" && draft.unitCost
                ? draft.unitCost
                : undefined,
            reason: draft.note.trim(),
          });
        case "transfer":
          return apiPost("/inventory/transfers", {
            ...base,
            fromLocationId: locationId,
            toLocationId: draft.toLocationId,
            note,
          });
        default:
          return apiPost(`/inventory/${operation}s`, {
            ...base,
            locationId,
            projectId: draft.projectId,
            note,
          });
      }
    },
    onSuccess: async () => {
      await Promise.all(
        [
          ["inventory-balances"],
          ["inventory-movements"],
          ["catalog"],
          ["project-financials"],
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  const locationOptions = activeLocations.map((location) => ({
    value: location.id,
    label: `${location.code} · ${location.name}`,
  }));
  const valid =
    draft.catalogItemId &&
    locationId &&
    Number(draft.quantity) > 0 &&
    (operation !== "transfer" ||
      (draft.toLocationId && draft.toLocationId !== locationId)) &&
    (!needsProject || draft.projectId) &&
    (operation !== "adjustment" || draft.note.trim());

  return (
    <Modal open onClose={onClose} title={t(`inventory.op.${operation}`)}>
      <form className="space-y-4" onSubmit={submit}>
        <MutationError error={save.error} />
        <Field label={t("inventory.item")} required>
          <SelectInput
            value={draft.catalogItemId}
            onChange={(value) => set("catalogItemId", value)}
            filter
            placeholder={t("inventory.selectItem")}
            options={(items.data ?? []).map((item) => ({
              value: item.id,
              label: `${item.sku ? `${item.sku} · ` : ""}${item.name}`,
            }))}
          />
        </Field>
        {needsProject && !projectId ? (
          <Field label={t("projects.project")} required>
            <SelectInput
              value={draft.projectId}
              onChange={(value) => set("projectId", value)}
              filter
              placeholder={t("planner.selectProject")}
              options={(projects.data?.data ?? [])
                .filter(
                  (node) =>
                    node.data.status !== "completed" &&
                    node.data.status !== "cancelled",
                )
                .map((node) => ({
                  value: node.data.id,
                  label: `${node.data.number} · ${node.data.title}`,
                }))}
            />
          </Field>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label={
              operation === "transfer"
                ? t("inventory.fromLocation")
                : operation === "return"
                  ? t("inventory.intoLocation")
                  : t("inventory.location")
            }
            required
          >
            <SelectInput
              value={locationId}
              onChange={(value) => set("locationId", value)}
              options={locationOptions}
            />
          </Field>
          {operation === "transfer" ? (
            <Field label={t("inventory.toLocation")} required>
              <SelectInput
                value={draft.toLocationId}
                onChange={(value) => set("toLocationId", value)}
                options={locationOptions.filter(
                  (option) => option.value !== locationId,
                )}
                placeholder={t("inventory.toLocation")}
              />
            </Field>
          ) : null}
          {operation === "adjustment" ? (
            <Field label={t("inventory.direction")} required>
              <SelectInput
                value={draft.direction}
                onChange={(value) => set("direction", value)}
                options={[
                  { value: "in", label: t("inventory.directionIn") },
                  { value: "out", label: t("inventory.directionOut") },
                ]}
              />
            </Field>
          ) : null}
          <Field label={t("inventory.quantity")} required>
            <Input
              fluid
              inputMode="decimal"
              value={draft.quantity}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                set("quantity", event.target.value.replace(",", "."))
              }
            />
          </Field>
          {operation === "receipt" ||
          (operation === "adjustment" && draft.direction === "in") ? (
            <Field label={t("inventory.unitCost")}>
              <Input
                fluid
                inputMode="decimal"
                value={draft.unitCost}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("unitCost", event.target.value.replace(",", "."))
                }
              />
            </Field>
          ) : null}
        </div>
        <Field
          label={
            operation === "adjustment"
              ? t("inventory.reason")
              : t("common.notes")
          }
          required={operation === "adjustment"}
        >
          <Input
            fluid
            value={draft.note}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              set("note", event.target.value)
            }
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={!valid} loading={save.isPending}>
            {t(`inventory.op.${operation}`)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function OperationButtons({
  onStart,
  exclude = [],
}: {
  onStart: (operation: Operation) => void;
  exclude?: Operation[];
}) {
  const { t } = useTranslation();
  const operations = useOperations().filter((op) => !exclude.includes(op));
  return (
    <div className="flex flex-wrap gap-2">
      {operations.map((operation, index) => (
        <Button
          key={operation}
          type="button"
          size="small"
          icon={OPERATION_ICON[operation]}
          variant={index === 0 ? undefined : "outlined"}
          severity={index === 0 ? undefined : "secondary"}
          onClick={() => onStart(operation)}
        >
          {t(`inventory.op.${operation}`)}
        </Button>
      ))}
    </div>
  );
}

export function LocationFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const locations = useLocations();
  return (
    <SelectInput
      ariaLabel={t("inventory.location")}
      value={value}
      onChange={onChange}
      placeholder={t("inventory.allLocations")}
      options={[
        { value: "", label: t("inventory.allLocations") },
        ...(locations.data ?? []).map((location) => ({
          value: location.id,
          label: `${location.code} · ${location.name}`,
        })),
      ]}
    />
  );
}
